import React, { useMemo } from 'react';
import type { MenuOption } from '../../components/OptionsMenu/OptionsMenu.types';
import type {
  Comment,
  UserRole
} from '../../containers/CommentList/CommentList.types';

// Export the hasUnreadMentions function so it can be reused elsewhere
export const hasUnreadMentionsForUser = (
  comment: Comment,
  loggedInuserEmail?: string
): boolean => {
  if (!loggedInuserEmail) return false;

  const lastReadTime = comment.readByMentionTimestamp
    ? new Date(comment.readByMentionTimestamp).getTime()
    : 0;

  // Check main comment
  const mainCommentTime = new Date(
    comment.editedAt || comment.createdAt
  ).getTime();
  const hasMainMention = comment.commentMentions?.some(
    mention => mention.email === loggedInuserEmail
  );

  if (hasMainMention && mainCommentTime > lastReadTime) {
    return true;
  }

  // Check replies - any reply with mention after lastReadTime is unread
  if (comment.replies) {
    return comment.replies.some(reply => {
      const replyTime = new Date(reply.editedAt || reply.createdAt).getTime();
      const hasReplyMention = reply.commentMentions?.some(
        mention => mention.email === loggedInuserEmail
      );
      return hasReplyMention && replyTime > lastReadTime;
    });
  }

  return false;
};

interface UseCommentMenuProps {
  showMenu?: boolean;
  menuOptions?: (string | MenuOption)[];
  resolvedCommentMenuOptions?: (string | MenuOption)[];
  replyMenuOptions?: (string | MenuOption)[];
  loggedInuserEmail?: string;
  authorRestrictedOptions?: string[];
  /** Option IDs that ADMIN can only perform when author (in addition to "edit", which is always restricted). E.g. ["delete"]. */
  adminRestrictedOptions?: string[];
  userRole?: UserRole;
}

export const useCommentMenu = ({
  showMenu = true,
  menuOptions = ['Edit', 'Delete', 'Resolve'],
  resolvedCommentMenuOptions = ['Unresolve', 'Delete'],
  replyMenuOptions = ['Edit', 'Delete'],
  loggedInuserEmail,
  authorRestrictedOptions = ['edit', 'delete', 'resolve', 'unresolve'],
  adminRestrictedOptions = [],
  userRole
}: UseCommentMenuProps) => {
  // Helper to convert string or MenuOption to MenuOption (DRY principle)
  const normalizeMenuOption = (option: string | MenuOption): MenuOption => {
    if (typeof option === 'string') {
      return {
        id: option.toLowerCase(),
        label: option
        // No icon for string options - only show icons when explicitly passed as MenuOption
      };
    }
    return option;
  };

  /**
   * Permission checker based on role and authorship
   * - Author can always do authorRestrictedOptions on their own comments (regardless of role)
   * - ADMIN can do everything except adminRestrictedOptions on others' comments (those require isAuthor)
   * - RESOLVER can only resolve/unresolve (but author can still edit/delete their own)
   * - VIEWER has no permissions (read-only)
   */
  const canPerformAction = (
    action: string,
    isAuthor: boolean,
    role?: UserRole
  ): boolean => {
    // If no role provided, use default author-based logic
    if (!role) {
      return !authorRestrictedOptions.includes(action) || isAuthor;
    }

    // Role-based permission matrix
    switch (role) {
      case 'ADMIN':
        // ADMIN: Edit is always author-only (attribution). adminRestrictedOptions adds more (e.g. delete).
        const adminRestricted = ['edit', ...adminRestrictedOptions];
        if (adminRestricted.includes(action)) return isAuthor;
        return true;
      case 'RESOLVER':
        // RESOLVER: Can only resolve/unresolve
        // BUT author can still edit/delete their own comments
        if (action === 'resolve' || action === 'unresolve') {
          return true;
        }
        if (action === 'edit' || action === 'delete') {
          return isAuthor;
        }
        // For any other actions, check if author or not restricted
        return !authorRestrictedOptions.includes(action) || isAuthor;

      case 'VIEWER':
        // VIEWER: Read-only, no actions allowed
        return false;

      default:
        // Fallback to default author-based logic
        return !authorRestrictedOptions.includes(action) || isAuthor;
    }
  };

  // Create menu options for comments
  const getCommentMenuOptions = useMemo(() => {
    return (comment: Comment): MenuOption[] => {
      if (!showMenu) return [];

      const isAuthor = loggedInuserEmail
        ? comment.commentAuthor.email === loggedInuserEmail
        : false;
      const options: MenuOption[] = [];

      // Use different menu options based on comment status
      const currentMenuOptions =
        comment.commentStatus === 'RESOLVED'
          ? resolvedCommentMenuOptions
          : menuOptions;

      currentMenuOptions.forEach(option => {
        const normalizedOption = normalizeMenuOption(option);

        // Check permissions based on role and authorship
        if (!canPerformAction(normalizedOption.id, isAuthor, userRole)) {
          return;
        }

        options.push(normalizedOption);
      });

      // Note: "Mark as Read" functionality is now handled by comment click handler
      // No longer adding it as a menu option

      return options;
    };
  }, [
    showMenu,
    menuOptions,
    resolvedCommentMenuOptions,
    loggedInuserEmail,
    authorRestrictedOptions,
    adminRestrictedOptions,
    userRole
  ]);

  // Create menu options for replies
  const getReplyMenuOptions = useMemo(() => {
    return (reply: Comment): MenuOption[] => {
      if (!showMenu) return [];

      const isAuthor = loggedInuserEmail
        ? reply.commentAuthor.email === loggedInuserEmail
        : false;
      const options: MenuOption[] = [];

      replyMenuOptions.forEach(option => {
        const normalizedOption = normalizeMenuOption(option);

        // Check permissions based on role and authorship
        if (!canPerformAction(normalizedOption.id, isAuthor, userRole)) {
          return;
        }

        options.push(normalizedOption);
      });

      // Note: In timestamp approach, "Mark as Read" is only available on main comments
      // since it marks the entire thread as read. Individual replies don't need separate read actions.

      return options;
    };
  }, [
    showMenu,
    replyMenuOptions,
    loggedInuserEmail,
    authorRestrictedOptions,
    adminRestrictedOptions,
    userRole
  ]);

  return { getCommentMenuOptions, getReplyMenuOptions };
};
